import * as THREE from 'three';
import { appConfig, APP_TYPES } from '@config';

import { GeneralCalculationObject } from '@object-generators/general-calculation';
import { BracketAttachmentObject } from '@object-generators/bracket-attachment';
import { SafetyBarrierObject } from '@object-generators/safety-barrier';
import { RebarReinforcementObject } from '@object-generators/rebar-reinforcement';
import { WoodConnectionObject } from '@object-generators/wood-connection';

import ObjectView from './views/object-view';

import Store from '@store';
import { setPanelOptions } from '@reducers/options/panel/actions';
import { setGroundOptions } from '@reducers/options/ground/actions';
import { setScrewOptions } from '@reducers/options/screw/actions';
import { setBarrierOptions } from '@reducers/options/barrier/actions';
import { setBeamOptions } from '@reducers/options/beam/actions';
import { setReinforcementOptions } from '@reducers/options/reinforcement/actions';
// import AbstractGenerator from './misc/generator';
import { setSceneSpinner, SET_CALCULATE_RESPONSE } from '@reducers/web-services/calculate/actions';
import tabs from './../App/tabs';
import { HTMLEditableLabel, HTMLLabel } from './objects/html-label';
import { EventManager, EVENTS } from '@components/Events/events';
import { switchToReport3DDimensions } from '@reducers/options/actions';
import { resetFEMResults } from '@reducers/selectors/calculate/actions';
import { PerspectiveCameraTypes } from '@reducers/camera/actions';
import { setCladdingOptions } from '@reducers/options/cladding/actions';
import { setNailOptions } from '@reducers/options/nail/actions';

let instance = null;

class Scene {

  constructor() {
    if (!instance) {
      instance = this;
    }

    this.scene = null;
    this.object = null;

    this.units = null;

    return instance;
  }

  init(units) {
    const container = document.getElementById('object-view-container');

    this.scene = new ObjectView(Store.getState().camera, container);
    this.scene.animate();

    this.units = units;

    // window.addEventListener('resize', resizeContainerForMobile(this.scene.container, this.scene), false);
    EventManager.on(EVENTS.WINDOW_RESIZE, resizeContainerForMobile(this.scene.container, this.scene).bind(this));
    EventManager.emit(EVENTS.WINDOW_RESIZE); // fix panels-container height
  }

  update({ camera, options, main }) {
    this.updateCamera(camera);
    this.updateAppType(main.appType, {
      ...options,
      mode: main.mode,
      designStandard: main.localization.designStandard, // add designStandard for Masonry texture
      orientation: main.orientation
    });

    if (main.appType === APP_TYPES.SAFETY_BARRIER)
      this.updateObjectType(options.object.type || SafetyBarrierObject.types.EDGE_SLAB);
  }

  resetCamera() {
    let bbox3d = new THREE.Box3();
    for (let i = 0; i < this.scene.globalObject.children.length; i++) {
      this.scene.globalObject.children[i].traverseVisible(function (child) {
        if (child instanceof THREE.Mesh && !child.ignoreOnCameraReset) {
          bbox3d.expandByObject(child);
        }
      });
    }

    if (this.scene.camera.isOrthographicCamera) {
      // Convert our coordinate system to three js coordinate system
      var temp = bbox3d.min.z;
      bbox3d.min = new THREE.Vector3(bbox3d.min.x, -bbox3d.max.z, bbox3d.min.y);
      bbox3d.max = new THREE.Vector3(bbox3d.max.x, -temp, bbox3d.max.y);

      let size = new THREE.Vector3();
      bbox3d.getSize(size);
      // Temporary zoom to fit all objects in the scene
      var tempObjZoom = 1 / Math.min(
        (this.scene.camera.top - this.scene.camera.bottom) / size.y, 
        (this.scene.camera.right - this.scene.camera.left) / size.x);
      tempObjZoom = Math.max(tempObjZoom, 0.65);

      var tempPos, tempElem;
      for (let i = 0; i < this.scene.htmlLayer.htmlLayerElements.length; i++) {
        tempElem = this.scene.htmlLayer.htmlLayerElements[i];
        tempPos = new THREE.Vector3(tempElem.position.x || 0, -tempElem.position.z || 0, 0);

        let x, y;
        switch (tempElem.align) {
          case HTMLEditableLabel.types.align.CENTER: x = tempElem.getElementRect().width / 2; break;
          case HTMLEditableLabel.types.align.RIGHT: x = -tempElem.getElementRect().width; break; 
          case HTMLEditableLabel.types.align.LEFT:
          default: {
            x = -HTMLLabel.PADDING + tempElem.getElementRect().width;
            break;
          }
        }
        switch (tempElem.verticalAlign) {
          case HTMLEditableLabel.types.verticalAlign.BOTTOM: y = tempElem.getElementRect().height; break;
          case HTMLEditableLabel.types.verticalAlign.CENTER: y = tempElem.getElementRect().height / 2; break;
          default: y = -tempElem.getElementRect().height; break;
        }
        
        tempPos.x += (x * tempObjZoom) / Math.max(0.65, this.scene.camera.zoom);
        tempPos.y += (y * tempObjZoom) / Math.max(0.65, this.scene.camera.zoom);

        bbox3d.expandByPoint(tempPos);
      }
      bbox3d.getSize(size);
      // Temporary zoom to fit everything in the view
      var tempZoom = 1 / Math.min(
        (this.scene.camera.top - this.scene.camera.bottom) / size.y, 
        (this.scene.camera.right - this.scene.camera.left) / size.x);
        tempZoom = Math.max(tempZoom, 0.65);
      
      // Add margins
      bbox3d.expandByPoint(new THREE.Vector3(bbox3d.min.x -10*tempZoom, bbox3d.min.y -10 * tempZoom, bbox3d.min.z -10*tempZoom));
      bbox3d.expandByPoint(new THREE.Vector3(bbox3d.max.x +10*tempZoom, bbox3d.max.y +10 * tempZoom, bbox3d.max.z +10*tempZoom));

      let center = new THREE.Vector3(); 
      bbox3d.getCenter(center);
      bbox3d.getSize(size);

      // calculate camera view direction
      var unitDirVector = new THREE.Vector3();
      // Get vector from camera to object & convert it to unit vector
      unitDirVector.subVectors(this.scene.camera.position, this.scene.controls.target).normalize();
      // position the camera
      this.scene.camera.position.z = -center.y;
      this.scene.camera.position.x = center.x;

      // set camera's zoom
      this.scene.camera.zoom = Math.min(
        (this.scene.camera.top - this.scene.camera.bottom) / size.y, 
        (this.scene.camera.right - this.scene.camera.left) / size.x);

      // update min/max zoom based on the current view
      this.scene.controls.minZoom = Math.min(0.1, this.scene.camera.zoom); // default minimum is 0.1
      this.scene.controls.maxZoom = Math.max(8, this.scene.camera.zoom); // default maximum is 8

      // set target for controls
      this.scene.controls.target.set(center.x, 0, -center.y);
      this.scene.controls.update();
    } else if (this.scene.options.perspectiveCameraType && 
        this.scene.options.perspectiveCameraType === PerspectiveCameraTypes.focus_base_plate) { 
      this.updateCamera(this.scene.options);
    } else {
      this.scene.resetCamera();
      this.scene.camera.updateProjectionMatrix();
      var cameraDir = new THREE.Vector3();
      this.scene.camera.getWorldDirection(cameraDir);
      let center = new THREE.Vector3(); 
      bbox3d.getCenter(center);

      let projPlane = new THREE.Plane(cameraDir, center.distanceTo(new THREE.Vector3(0, 0, 0)));
      let projPnt = new THREE.Vector3();
      let pnt = null, indices = null, vertices = null;
      var minX = new THREE.Vector3(Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY);
      var minY = new THREE.Vector3(Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY);
      var minZ = new THREE.Vector3(Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY);
      var maxX = new THREE.Vector3(Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY);
      var maxY = new THREE.Vector3(Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY);
      var maxZ = new THREE.Vector3(Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY);
      function getMinMaxProjected(child) {
        if (child instanceof THREE.Mesh && !child.ignoreOnCameraReset) {
          indices = child.geometry.index;
          vertices = child.geometry.attributes.position;
          if (indices === null) { // non-indexed geometry
            for (let i = 0; i < vertices.count; i++) {
              pnt = child.localToWorld(new THREE.Vector3(vertices.getX(i), vertices.getY(i), vertices.getZ(i)));
              projPlane.projectPoint(pnt, projPnt);
              if (projPnt.x < minX.x)
                minX = projPnt.clone();
              if (projPnt.y < minY.y)
                minY = projPnt.clone();
              if (projPnt.z < minZ.z)
                minZ = projPnt.clone();
              if (projPnt.x > maxX.x)
                maxX = projPnt.clone();
              if (projPnt.y > maxY.y)
                maxY = projPnt.clone();
              if (projPnt.z > maxZ.z)
                maxZ = projPnt.clone();
            }
          } else { // indexed geometry
            for (let index = 0; index < indices.count; index++) {
              pnt = child.localToWorld(new THREE.Vector3(
                vertices.getX(indices.array[index]), 
                vertices.getY(indices.array[index]), 
                vertices.getZ(indices.array[index])
              ));
              projPlane.projectPoint(pnt, projPnt);
              if (projPnt.x < minX.x)
                minX = projPnt.clone();
              if (projPnt.y < minY.y)
                minY = projPnt.clone();
              if (projPnt.z < minZ.z)
                minZ = projPnt.clone();
              if (projPnt.x > maxX.x)
                maxX = projPnt.clone();
              if (projPnt.y > maxY.y)
                maxY = projPnt.clone();
              if (projPnt.z > maxZ.z)
                maxZ = projPnt.clone();        
            }
          }
        }
      }
      for (let i = 0; i < this.scene.globalObject.children.length; i++) {
        this.scene.globalObject.children[i].traverseVisible((child) => getMinMaxProjected(child));
      }

      center = new THREE.Vector3(); 
      center.x = (maxX.x + minX.x) / 2;
      center.y = (maxY.y + minY.y) / 2;
      center.z = (maxZ.z + minZ.z) / 2;

      // Added an extra 10% to Y dimension to have some space above and below the object for HTML labels
      let maxDim = Math.max(
        Math.sqrt(Math.pow((maxX.x - minX.x), 2) + Math.pow((maxX.y - minX.y), 2) + Math.pow((maxX.z - minX.z), 2)), 
        Math.sqrt(Math.pow((maxY.x - minY.x), 2) + Math.pow((maxY.y - minY.y), 2) + Math.pow((maxY.z - minY.z), 2)) * 1.1,
        Math.sqrt(Math.pow((maxZ.x - minZ.x), 2) + Math.pow((maxZ.y - minZ.y), 2) + Math.pow((maxZ.z - minZ.z), 2))) / 2;
      // DEBUG
      // let bboxHelper = new THREE.BoxHelper(this.scene.globalObject, 0xffff00);
      // bboxHelper.update();
      // this.scene.globalObject.add(bboxHelper);

      // let sphereGeom = new THREE.SphereGeometry(sphereRadius, 60, 60);
      // let material = new THREE.MeshBasicMaterial( {color: 0xffff00} );
      // this.debugMesh = new THREE.Mesh(sphereGeom, material);
      // this.debugMesh.position.set(center.x, center.y, center.z);
      // this.scene.globalObject.add(this.debugMesh);

      //const material = new THREE.LineBasicMaterial( { color: 0x0000ff } );
      //let geometry = new THREE.BufferGeometry().setFromPoints([minX, maxX, minY, maxY, minZ, maxZ]);
      //const line = new THREE.Line( geometry, material );
      //this.scene.globalObject.add(line);
      // ~DEBUG

      let vFoV = this.scene.camera.getEffectiveFOV();
      let hFoV = vFoV * this.scene.camera.aspect;
      let fov = Math.min(vFoV, hFoV) / 2 * (Math.PI / 180);
      let dist = maxDim / Math.sin(fov);

      this.scene.camera.position.set(center.x, center.y, center.z);
      this.scene.camera.position.addScaledVector(cameraDir, -dist);

      this.scene.camera.lookAt(center);

      if (dist > this.scene.controls.maxDistance)
        this.scene.controls.maxDistance = dist;
      this.scene.controls.target.set(center.x, center.y, center.z);
      this.scene.controls.update();
    }
    this.scene.camera.updateProjectionMatrix();
    EventManager.emit(EVENTS.CAMERA_RESET);

    /*
    // Debugging
    var debugcamera = new THREE.PerspectiveCamera(this.scene.camera.fov, 
      this.scene.camera.aspect,
      500, 3500);
    var cameraHelper = new THREE.CameraHelper( debugcamera );
    this.scene.scene.add( cameraHelper );
    var cameraEye = new THREE.Mesh( new THREE.SphereBufferGeometry( 5 ), new THREE.MeshBasicMaterial( { color: 0xff00ff } ) );
    cameraEye.position.copy(this.scene.controls.target);
    this.scene.scene.add( cameraEye );

    var helper = new THREE.Box3Helper( bbox3d, 0xffff00 );
    this.scene.scene.add( helper );
    var axisHelper = new THREE.AxisHelper(2*bbox3d.getBoundingSphere().radius);
    this.scene.scene.add(axisHelper);
    // ~Debugging
    */

    /*
        var center = new THREE.Vector3(); center.copy(bbox3d.getBoundingSphere().center);
        const size = bbox3d.getSize();
        const maxDim = Math.max( size.x, size.y, size.z );
        const fov = this.scene.camera.fov * ( Math.PI / 180 );
        let cameraZ = Math.abs( (maxDim / 2) / Math.tan( fov / 2 ) ); //Applied fifonik correction
        cameraZ *= 1.25; // zoom out a little so that objects don't fill the screen
        this.scene.scene.updateMatrixWorld(); //Update world positions
        var objectWorldPosition = new THREE.Vector3();
        objectWorldPosition.setFromMatrixPosition( this.scene.globalObject.matrixWorld );
     
        // Get vector from camera to object
        const directionVector = new THREE.Vector3(); directionVector.subVectors(this.scene.camera.position, objectWorldPosition); 	
        // Convert to unit vector
        const unitDirectionVector = new THREE.Vector3(); unitDirectionVector.copy(directionVector); unitDirectionVector.normalize(); 
        this.scene.camera.position.copy(unitDirectionVector);
        this.scene.camera.position.multiplyScalar(cameraZ); //Multiply unit vector times cameraZ distance
        this.scene.camera.lookAt(objectWorldPosition); //Look at object
        // --->
      
        const minZ = bbox3d.min.z;
        const cameraToFarEdge = ( minZ < 0 ) ? -minZ + cameraZ : cameraZ - minZ;
      
        this.scene.camera.far = cameraToFarEdge * 3;
        this.scene.camera.lookAt( center );
    
        
        /*
        var bbox_center = bbox3d.getBoundingSphere().center;
        var bbox_radius = bbox3d.getBoundingSphere().radius;
        var frustumHeight;
        var aspect = this.scene.width / this.scene.height;
    
        if (aspect > 1) {
          frustumHeight = 2.0 * bbox_radius;
        }
        else {
          frustumHeight = 2.0 * bbox_radius / aspect;
        }
    
        if (this.object.is2DMode) {
          this.scene.camera.left = bbox_center.x - frustumHeight * aspect / 2;
          this.scene.camera.right = bbox_center.x + frustumHeight * aspect / 2;
          this.scene.camera.top = bbox_center.y - frustumHeight / 2;
          this.scene.camera.bottom =  bbox_center.y + frustumHeight / 2;
        }
        else {
          var radFov = this.scene.camera.fov * Math.PI / 180;
          var dist = (bbox_radius / Math.tan(radFov * 0.5)) * this.scene.camera.aspect;
    
          //Get vector from camera to object
          var directionVector = new THREE.Vector3(); 
          directionVector.subVectors(this.scene.camera.position, bbox_center); 	//Get vector from camera to object
          const unitDirectionVector = directionVector.normalize(); // Convert to unit vector
          //Multiply unit vector times cameraZ distance
          // this.scene.camera.position.setZ(dist); 
          // this.scene.camera.lookAt(bbox_center); //Look at object
          // --->
          var arrowHelper = new THREE.ArrowHelper( unitDirectionVector.negate(), this.scene.camera.position, dist, 0xff00ff);
          this.scene.scene.add( arrowHelper );
          
          // const minZ = bbox3d.min.z;
          // const cameraToFarEdge = ( minZ < 0 ) ? -minZ + dist : dist - minZ;
          // this.scene.camera.far = cameraToFarEdge * 3;
        }
        */
  }

  updateCamera(options) {
    this.scene.update(options);
  }

  updateAppType(appType, options) {
    this.object = getObject(appType, options);
    // this.scene.addObject(this.object);
    this.updateMode(options.mode);
  }

  updateObjectType(type) {
    this.updateObject({ type });
    this.scene.addObject(this.object);
  }

  updateMode(mode) {
    this.scene.setMode(mode);
    this.updateObject();
    // wait for the object to update (used mostly when switching between applications)
    setTimeout(() => this.resetCamera());
  }

  updateObject(options) {
    if (this.object) {
      this.object.update(options);
      this.scene.addObject(this.object);

      if (this.object.draggableObjects && this.object.draggableObjects.length > 0) {
        let dragObjects = this.scene.dragControls.getObjects();
        dragObjects.length = 0;
        dragObjects.push(...this.object.draggableObjects);
        this.scene.dragControls.enabled = true;
        this.scene.dragControls.activate(); // add event listeners
      } else if (this.scene.dragControls.enabled) {
        this.scene.dragControls.enabled = false;
        this.scene.dragControls.deactivate(); // remove event listeners
      }
    }
  }

  updatePanel(options) {
    this.updateObject({ panel: options });
  }

  updateScrew(options) {
    this.updateObject({ screw: options });
  }

  updateBeam(options) {
    this.updateObject({ beam: options });
  }

  updateBarrier(options) {
    this.updateObject({ barrier: options });
  }

  updateGround(options) {
    this.updateObject({ ground: options });
  }

  updateReinforcement(options) {
    this.updateObject({ reinforcement: options });
  }

  updateObjectOrientation(orientation) {
    this.updateObject({ orientation });
  }
  
  updateNail(options) {
    this.updateObject({ nail: options });
  }
  
  updateWoodConnection(options) {
    this.updateObject({ woodPanel: options });
  }


  updateCladding(options) {
    this.updateObject({ cladding: options});
  }

  updateAxis(options) {
    this.scene.updateAxis(options);
  }

  updateAxisOrientation(orientation) {
    this.scene.axis.setAxisOrientation(orientation);
  }

  getSnapshots(cb) {
    const currentMode = this.scene.mode;
    var prevWidth = this.scene.width;
    var prevHeight = this.scene.height;
    var prevPixelRatio = this.scene.renderer.getPixelRatio();
    this.scene.resize(1280, 720); // 720p resolution
    // this.scene.resize(1920, 1080); // 1080p resolution - texts too small on 2D
    this.scene.renderer.setPixelRatio(1);
    this.updateMode(ObjectView.mode.MODE_3D);

    EventManager.once(EVENTS.CAMERA_RESET, (async () => {
      // camera was reset take first snapshot
      this.scene.takeSnapshot("LOADS3D", cb);
      this.updateMode(ObjectView.mode.MODE_2D);
      Store.dispatch(switchToReport3DDimensions(false)); // we can switch to previous settings now
      EventManager.once(EVENTS.CAMERA_RESET, (async () => {
        // camera was reset take second snapshot
        this.scene.takeSnapshot("GEOMETRY2D", cb);
        this.scene.resize(prevWidth, prevHeight);
        this.scene.renderer.setPixelRatio(prevPixelRatio);
        this.updateMode(currentMode);
        EventManager.once(EVENTS.CAMERA_RESET, (async () => {
          // remove the report spinner after going back to the initial state
          Store.dispatch(setSceneSpinner(false));
        }));
      }));
    }));
  }

  updateUnits(units) {
    this.units = units;

    this.updateObject({});
  }

  onUpdate = onUpdate;
  
}

export default new Scene();

function resizeContainerForMobile(container, scene) {
  const headerHeight = document.querySelector('.header .navbar').clientHeight;
  const mainContainer = document.querySelector('.application');
  const panelsContainer = document.querySelector('.application .panel-container');
  
  return function () {
    // VladP: Must query everytime, because it will take into account only the initial value (screen < 1000px -> height = 0)
    let unitsHeight = document.querySelector('.app__units-row').clientHeight;
    if (window.innerWidth <= 1000) {
      let optionsButton = document.querySelector('.application .application__tabs .tabs__button');
      mainContainer.style.height = `${window.innerHeight - headerHeight}px`;
      optionsButton.style.position = 'absolute';
      optionsButton.style.top = `${window.innerHeight - (optionsButton ? optionsButton.clientHeight:0)}px`;
      scene.resize(
        container.parentNode.clientWidth,
        window.innerHeight - headerHeight - (optionsButton ? optionsButton.clientHeight:0)
      );
    } else {
      mainContainer.style.height = `${window.innerHeight - headerHeight - unitsHeight}px`;
      scene.resize(
        container.parentNode.clientWidth - 8,// resizer width
        window.innerHeight - headerHeight - unitsHeight
      );
    }
    panelsContainer.style.height = mainContainer.style.height;

    this.resetCamera();
  };
}

function getObject(type = appConfig.APP_TYPES.GENERAL_CALCULATION, options) {
  switch (type) {
    case appConfig.APP_TYPES.BRACKET_ATTACHMENT: {
      const object = new BracketAttachmentObject(options);
      object.onOptionUpdate(onUpdate);

      return object;
    }
    case appConfig.APP_TYPES.SAFETY_BARRIER: {
      const object = new SafetyBarrierObject({
        ...options,
        panel: {
          ...options.panel//,
          //screw: options.screw
        },
        loads: options.object.loads
      });
      object.onOptionUpdate(onUpdate);

      return object;
    }
    case appConfig.APP_TYPES.REBAR_REINFORCEMENT: {
      const object = new RebarReinforcementObject({
        ...options
      });
      object.onOptionUpdate(onUpdate);

      return object;
    }
    case appConfig.APP_TYPES.WOOD_CONNECTION: {
      const object = new WoodConnectionObject({
        ...options
      });
      object.onOptionUpdate(onUpdate);
      return object;
    }
    case appConfig.APP_TYPES.GENERAL_CALCULATION:
    default: {
      const object = new GeneralCalculationObject(options);
      object.onOptionUpdate(onUpdate);
      object.mesh.rotation.z = Math.PI / 2;

      return object;
    }
  }
}

function onUpdate(options, ignoreObjectUpdate, resetCalculateResults) {
  if (Store.getState().main.activeTab === tabs.CALCULATE && Store.getState().webServices.calculate.isWaiting)
    return; // prevent update if a calculation is running and the user is patiently waiting

  if (options.panel)
    Store.dispatch(setPanelOptions(options.panel, ignoreObjectUpdate));
  if (options.ground) 
    Store.dispatch(setGroundOptions(options.ground));
  if (options.screw)
    Store.dispatch(setScrewOptions(options.screw, ignoreObjectUpdate));
  if (options.barrier)
    Store.dispatch(setBarrierOptions(options.barrier));
  if (options.beam)
    Store.dispatch(setBeamOptions(options.beam));
  if (options.reinforcement)
    Store.dispatch(setReinforcementOptions(options.reinforcement));
  if (options.nail)
    Store.dispatch(setNailOptions(options.nail));
  if (options.bracket)
    Store.dispatch(setPanelOptions(options.bracket));
  if (options.wood)
    Store.dispatch(setPanelOptions(options));

  if (options.cladding) {
    Store.dispatch(setCladdingOptions(options.cladding));
  }

  if (resetCalculateResults && Store.getState().main.activeTab === tabs.CALCULATE) {
    (async () => {
      await Store.dispatch((dispatch) => {
        dispatch({ type: SET_CALCULATE_RESPONSE, res: [] }); // reset results
        dispatch(resetFEMResults());
      });
    })();
  }
} 



// WEBPACK FOOTER //
// ./src/object-view/index.js